<template>
  <DpPanel>
    <template v-slot:header>
      <h1 class="text-center font-weight-bold">{{ $t('ownerPanel.title') }}</h1>
      <p v-html="$t('ownerPanel.contactInfoRequired')" />
      <h2 class="text-primary mt-4 h3">
        {{ $t('welcomePanel.whereLiveWithPet') }}
      </h2>
      <p class="my-2">
        {{ $t('ownerPanel.howToPopulateAddress') }}
      </p>
    </template>

    <template v-slot:content>
      <b-row v-if="duplicatePetError && duplicatePetError.code !== 'unprocessable_entity'">
        <b-col>
          <b-card border-variant="warning" class="mb-4 mt-3">
            <b-card-text class="text-dark">
              <p v-html="duplicatePetError.message"></p>
              <p class="mb-0">{{ $t('errorMessages.duplicatePetCancelRegistration') }}</p>
              <b-button variant="primary" class="mt-3 w-100" @click="cancelRegistration">{{ $t('cancelRegistration') }}</b-button>
            </b-card-text>
          </b-card>
        </b-col>
      </b-row>

      <ValidationObserver ref="form" v-slot="{ handleSubmit, invalid }">
        <form @submit.prevent="handleSubmit(onSubmit)">
          <DpAddressSearchManual
            :id="0"
            ref="homeAddress"
            :country="petOwnerCountry"
            :existing-address-label="petOwner && petOwner.address ? petOwner.address.display : null"
            :existing-address="petOwner && petOwner.address ? petOwner.address : null"
            @updateAddress="updateAddress"
          />

          <b-row v-show="isDuplicatePetCheckLoading || isSuggestedJurisdictionsLoading">
            <b-col class="text-center my-4">
              <b-spinner />
            </b-col>
          </b-row>

          <b-alert
            variant="danger"
            v-if="isAddressValidationError && !(isDuplicatePetCheckLoading || isSuggestedJurisdictionsLoading)"
            show
          >
            {{ $t('input.addressValidationError') }}
          </b-alert>

          <b-row v-if="isSuggestingJurisdictions && !isSuggestedJurisdictionsLoading" class="mb-4">
            <b-col>
              <dp-suggested-jurisdictions-radio
                :suggested-jurisdictions="suggestedJurisdictions"
                :title="suggestedJurisdictionsHeaderHtml"
                :terms="suggestedJurisdictionsTerms"
                :address="homeAddressString"
                :g-maps-api-key="gmapsApiKey"
                :g-maps-map-id="gmapsBoundaryMapId"
                :jurisdiction-boundaries="suggestedJurisdictionsBoundaries"
                :non-licensing-html="suggestedJurisdictionsNonLicensingHtml"
                :non-licensing-learn-more-html="suggestedJurisdictionsNonLicensingLearnMoreHtml"
                :non-licensing-fallback-option-label="suggestedJurisdictionsNonLicensingFallbackOptionLabel"
                :fallback-jurisdiction="suggestedJurisdictionsFallbackJurisdiction"
                @selectionChanged="handleSuggestedJurisdictionsSelectionChanged"
              ></dp-suggested-jurisdictions-radio>
            </b-col>
          </b-row>

          <b-row class="my-4">
            <b-col>
              <b-form-checkbox v-model="sameMailing" :value="true" :status="sameMailing" :unchecked-value="false">
                {{ $t('welcomePanel.mailingAddressSame') }}
              </b-form-checkbox>
            </b-col>
          </b-row>

          <p v-if="!sameMailing" class="text-primary mt-4 h3">
            {{ $t('input.mailingAddress.label') }}
          </p>

          <DpAddressSearchManual
            v-if="!sameMailing"
            :id="1"
            :selected-type="$t('welcomePanel.mailingAddress')"
            :country="petOwnerCountry"
            :existing-address-label="mailingDisplay"
            :allow-additional-field="true"
            :existing-address="petOwner && petOwner.mailingAddress ? petOwner.mailingAddress : null"
            @updateAddress="updateMailing"
          />

          <p class="w-100 mb-2 h3">
            {{ $t('input.phoneNumber.primaryPhone.label') }}
          </p>
          <p>
            {{ $t('welcomePanel.phoneNumberText') }}
          </p>
          <BaseVPhoneNumbers
            v-model="phoneNumbers"
            :mobile-phone="cellPhone"
            :home-phone="phone"
            :work-phone="workPhone"
            :phone-numbers-has-primary="true"
            :primary-number-type="petOwner && petOwner.primaryNumberType ? petOwner.primaryNumberType : 'M'"
          >
            <template #optionalNumbersHeader>
              <b-row no-gutters>
                <div class="d-flex justify-content-between flex-grow-1 mt-2">
                  <p class="w-100 h3">
                    {{ $t('input.phoneNumber.secondaryPhone.add') }}
                  </p>
                </div>
                <div class="form-text text-muted mt-2">
                  <small>{{ $t('optional') }}</small>
                </div>
              </b-row>
            </template>
          </BaseVPhoneNumbers>

          <b-row class="mt-4">
            <b-button
              class="loading-button-container"
              type="submit"
              variant="primary"
              :disabled="invalid || isButtonDisabled || Boolean(duplicatePetError)"
              block
            >
              {{ $t('continue') }}
            </b-button>
          </b-row>
        </form>
      </ValidationObserver>
    </template>
  </DpPanel>
</template>

<script>
import { AddressMixins } from '@/mixins';
import { createNamespacedHelpers, mapState } from 'vuex';
import { DpPanel, DpAddressSearchManual, DpSuggestedJurisdictionsRadio } from '@docupet/component-library';
const { mapState: mapPetOwnerState, mapGetters: mapPetOwnerGetters, mapActions: mapPetOwnerActions } = createNamespacedHelpers('register');

export default {
  name: 'ContactDetails',
  components: {
    DpAddressSearchManual,
    DpPanel,
    DpSuggestedJurisdictionsRadio,
  },
  mixins: [AddressMixins],
  props: {},
  data() {
    return {
      phone: '',
      cellPhone: '',
      workPhone: '',
      phoneNumbers: {},
      address: {
        civic: '',
        street: '',
        unit: '',
        additional: '',
        city: '',
        province: '',
        postalCode: '',
      },
      sameMailing: true,
      mailingAddress: {
        civic: '',
        street: '',
        unit: '',
        additional: '',
        city: '',
        province: '',
        postalCode: '',
      },
      submitted: true,
      updatedHomeAddress: false,
      updatedMailingAddress: false,
      isAddressValidationError: null,
      isDuplicatePetCheckLoading: false,
    };
  },
  computed: {
    ...mapState({
      suggestedJurisdictionId: (state) => state.register?.suggestedJurisdiction,
      suggestedCity: (state) => state.register?.suggestedCity,
      suggestedJurisdictions: (state) => state.register?.suggestedJurisdictions,
      suggestedJurisdictionsHeaderHtml: (state) => state.register?.suggestedJurisdictionsHeaderHtml,
      suggestedJurisdictionsTerms: (state) => state.register?.suggestedJurisdictionsTerms,
      suggestedJurisdictionsNonLicensingHtml: (state) => state.register?.suggestedJurisdictionsNonLicensingHtml,
      suggestedJurisdictionsNonLicensingLearnMoreHtml: (state) => state.register?.suggestedJurisdictionsNonLicensingLearnMoreHtml,
      suggestedJurisdictionsNonLicensingFallbackOptionLabel: (state) =>
        state.register?.suggestedJurisdictionsNonLicensingFallbackOptionLabel,
      suggestedJurisdictionsFallbackJurisdiction: (state) => state.register?.suggestedJurisdictionsFallbackJurisdiction,
      isSuggestedJurisdictionsLoading: (state) => state.register?.suggestedJurisdictionsLoading,
      suggestedJurisdictionsBoundaries: (state) => state.register?.suggestedJurisdictionsBoundaries,
      gmapsApiKey: (state) => state.register?.suggestedJurisdictionsGmapsApiKey,
      gmapsBoundaryMapId: (state) => state.register?.suggestedJurisdictionsGmapsBoundaryMapId,
    }),
    ...mapPetOwnerGetters(['loading', 'success', 'error', 'neverLoaded', 'getToken', 'getBaseUrl']),
    ...mapPetOwnerState({
      petOwner: (state) => (state.petOwner ? state.petOwner : null),
      pets: (state) => state.pets,
      duplicatePetError: (state) => state.duplicatePetError,
      baseUrl: (state) => state.baseUrl,
    }),
    isSuggestingJurisdictions() {
      return Array.isArray(this.suggestedJurisdictions) && this.suggestedJurisdictions?.length > 0;
    },
    isHomeAddressFilled() {
      return this.isAddressFilled(this.address);
    },
    isMailingAddressFilled() {
      return this.isAddressFilled(this.mailingAddress);
    },
    mailingDisplay() {
      return this.petOwner?.mailingAddress !== null ? this.formatAddressDisplay(this.petOwner.mailingAddress) : '';
    },
    getPrimaryNumberType() {
      return Object.keys(this.phoneNumbers).find((phoneNumberType) => this.phoneNumbers[phoneNumberType]?.isPrimaryPhoneType === true);
    },
    brandingPhone() {
      return this.petOwner?.city?.branding?.phone;
    },
    petOwnerCountry() {
      return this.petOwner?.city?.country || null;
    },
    isButtonDisabled() {
      return (
        Boolean(this.isAddressValidationError) ||
        Boolean(this.duplicatePet) ||
        this.isDuplicatePetCheckLoading ||
        this.isSuggestedJurisdictionsLoading ||
        (this.isSuggestingJurisdictions && !this.suggestedJurisdictionId)
      );
    },
    homeAddressString() {
      return `${this.address.civic} ${this.address.street}, ${this.address.cityName}, ${this.address.province} ${this.address.postalCode}`;
    },
  },
  methods: {
    ...mapPetOwnerActions(['load', 'setAddresses', 'addPetOwnerContact', 'resetState']),
    resetSuggestedJurisdictions() {
      this.$store.commit('register/SET_SUGGESTED_JURISDICTIONS_TERMS', []);
      this.$store.commit('register/SET_SUGGESTED_JURISDICTIONS', []);
      this.$store.commit('register/SET_SUGGESTED_JURISDICTIONS_LOADING', false);
      this.$store.commit('register/SET_SUGGESTED_JURISDICTION', null);
      this.$store.commit('register/SET_SUGGESTED_CITY', null);
      this.$store.commit('register/SET_SUGGESTED_JURISDICTIONS_FALLBACK_JURISDICTION', null);
      this.$store.commit('register/SET_SUGGESTED_JURISDICTIONS_NON_LICENSING_LEARN_MORE_HTML', null);
      this.$store.commit('register/SET_SUGGESTED_JURISDICTIONS_NON_LICENSING_HTML', null);
      this.$store.commit('register/SET_SUGGESTED_JURISDICTIONS_NON_LICENSING_FALLBACK_OPTION_LABEL', null);
    },
    fixProvince(addressType) {
      let country = this.petOwner?.city?.country ?? null;

      if (this.petOwner && this.petOwner[addressType]) {
        let province = this.petOwner[addressType].province;
        if (country && province.length <= 2) {
          province = country + '_' + province;
        }
        this[addressType].province = province || null;
      }
    },
    cancelRegistration() {
      this.resetState();
      let cancelURL = this.baseUrl || window.location.origin;
      window.location.href = cancelURL + '/logout-cancel-account';
    },
    async updateAddress(value) {
      this.isAddressValidationError = false;
      this.resetSuggestedJurisdictions();
      this.updatedHomeAddress = true;
      this.address = value;
      this.fixProvince('address');

      if (this.petOwner.address && !this.isHomeAddressFilled) {
        return;
      }

      try {
        this.isDuplicatePetCheckLoading = true;
        await this.$store.dispatch('register/checkForDuplicatePet', this.toAddressObject(this.address));
      } catch (e) {
        this.isAddressValidationError = true;
      } finally {
        this.isDuplicatePetCheckLoading = false;
      }

      if (this.duplicatePetError || this.isAddressValidationError) {
        return;
      }

      try {
        await this.$store.dispatch('register/getSuggestedJurisdictions', this.toAddressObject(this.address));
      } catch (e) {
        this.isAddressValidationError = true;
      }
    },
    updateMailing(value) {
      this.updatedMailingAddress = true;
      this.mailingAddress = value;
      this.fixProvince('mailingAddress');
    },
    handleSuggestedJurisdictionsSelectionChanged(selectedJurisdictionId) {
      const suggestedJurisdiction = this.$store.getters['register/getSuggestedJurisdictionById'](selectedJurisdictionId);
      if (!suggestedJurisdiction) {
        return;
      }
      this.$store.commit('register/SET_SUGGESTED_CITY', suggestedJurisdiction?.city);
      this.$store.commit('register/SET_SUGGESTED_JURISDICTION', suggestedJurisdiction?.id);
    },
    onSubmit() {
      this.$refs.form.validate().then(async (success) => {
        // Validate Forms if hidden "Validate Values"
        if (success && this.isHomeAddressFilled && (this.isMailingAddressFilled || this.sameMailing)) {
          if (this.updatedHomeAddress || this.updatedMailingAddress) {
            await this.setAddresses({
              home: this.toAddressObject(this.isHomeAddressFilled ? this.address : this.petOwner.address),
              mailing: this.toAddressObject(this.sameMailing ? null : this.mailingAddress),
              continueLoading: true,
              jurisdictionId: this.suggestedJurisdictionId,
            });
          }
        }

        await this.addPetOwnerContact({
          phone: this.phoneNumbers?.['H']?.value || '',
          email: this.petOwner?.email,
          cellPhone: this.phoneNumbers?.['M']?.value || '',
          workPhone: this.phoneNumbers?.['W']?.value || '',
          primaryNumberType: this.getPrimaryNumberType,
          continueLoading: true,
        }).then(() => {
          sessionStorage.clear();
          this.$router.push({
            name: 'SymActivateTagRedirect',
            query: { token: this.getToken },
          });
        });
      });
    },
  },
  async created() {
    this.resetSuggestedJurisdictions();
    this.load()
      .then((po) => {
        this.phone = po?.phone;
        this.cellPhone = po?.cellPhone;
        this.workPhone = po?.workPhone;
        this.address = po?.address;
        this.sameMailing = po?.mailingAddress == null;
        this.mailingAddress = po?.mailingAddress == null ? null : po?.mailingAddress;
        this.$nextTick(() => {
          this.fixProvince('address');
          this.fixProvince('mailingAddress');
        });
      })
      .catch(() => {
        this.$router.push({
          name: 'BaseURL',
          query: { token: this.getToken },
        });
      });

    window.onhashchange = function () {
      return 'Your work will be lost.';
    };
  },
};
</script>

<style lang="scss" scoped>
@import '@docupet/component-library/src/assets/styles/_variables';
.civic-input {
  width: 90px;
  float: left;
}

.postal-fields {
  width: 50%;
}

.error-message {
  text-align: center;
  color: $dp-red;
}
</style>
